// INCOGNITO, ON THE WIRE (busy-my-web#407 web half / busy-main-bff#43, 2026-09-15 parity audit).
//
// store/modules/incognito.js says the mode is CLIENT-SIDE ONLY and that a real incognito needs a
// do-not-persist flag on the chat/search calls so the BFF writes no story and no timeline row.
// busy-main-bff#43 is that flag: the header `X-Busy-Incognito: 1` on every ask / search / chat
// request made while the mode is on. This module is the web half of it, and it is deliberately tiny
// and dependency-free for one reason — the axios interceptor cannot import the Vuex store (the
// store imports the modules that import the service layer that imports the interceptor, and a
// static import there is an initialisation cycle at boot). So the store PUSHES the flag in here,
// and the request layer READS it out.
//
// DEFENSIVE: the header is additive. A BFF that has not shipped #43 yet ignores an unknown header,
// and nothing in the app changes when the mode is off — `incognitoHeaders` returns an empty object,
// so no call site has to branch.

/** Module-level, not reactive: this is read from inside a request interceptor, not a template. */
let on = false

/** Pushed by the incognito store whenever the mode is turned on or off. */
export function setIncognitoRequests(value) { on = !!value }

/** Is the mode on? (exported for the tests and for anything that wants to ask without the store) */
export function incognitoRequests() { return on }

/**
 * WHICH calls carry the flag.
 *
 * Only the ones the mode is ABOUT — asking, searching and the chat session itself. A file upload,
 * a task update or a calendar read are not "this conversation", and marking them incognito would
 * tell the server to drop work the user explicitly asked it to keep. Matched on the path rather
 * than on a call-site list so a new ask surface is covered the day it is written.
 */
const INCOGNITO_PATHS = [
  /(^|\/)ai\//i,          // ai/ask, ai/analyze-timeline/stream, …
  /(^|\/)chat\//i,        // chat/sessions…
  /(^|\/)intelligent\/ask(\/|$)/i, // the chat answered by busy-intelligent-srv
  /(^|\/)research(\/|$)/i,
  /(^|\/)timeline-search(\/|$)/i,
  /(^|\/)suggest(\?|$|\/)/i,
  /(^|\/)search(\/|$)/i,
]

/** Does this URL belong to the ask / search / chat family? */
export function isIncognitoPath(url) {
  const path = String(url || '').split('?')[0]
  if (!path) return false
  return INCOGNITO_PATHS.some(re => re.test(path))
}

/**
 * The headers to merge into a request — `{}` unless the mode is on AND the call is one of ours.
 * `{}` rather than `null` so every call site can spread it unconditionally.
 */
export function incognitoHeaders(url) {
  return on && isIncognitoPath(url) ? { 'X-Busy-Incognito': '1' } : {}
}

/** The header's name and value, in one place, for the tests and the interceptor. */
export const INCOGNITO_HEADER = 'X-Busy-Incognito'
